import type { Custodian } from 'models/custodian';
import { ReactNode } from 'react';
import type { MapStateToPropsFactory } from 'react-redux';
import { createSelector } from 'reselect';
import type { LimitOrderInputFormSelectors } from 'state/secondaryMarket/limit-order/form/input/inputForm.selectors';
import type { LimitOrderSelectors } from 'state/secondaryMarket/limit-order/limitOrder.selector';
import type { LimitOrderThunks } from 'state/secondaryMarket/limit-order/limitOrder.thunk';
import type { LiveInputFormSelectors } from 'state/secondaryMarket/live/form/input/inputForm.selectors';
import type { LiveSelectors } from 'state/secondaryMarket/live/live.selector';
import type { LiveThunks } from 'state/secondaryMarket/live/live.thunk';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import type { ReferentialSelector } from 'state/secondaryMarket/shared/referential/referential.selector';
import type { TradeSizeInputMode } from 'state/secondaryMarket/shared/tradeInformation.models';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { SecondaryWorkspaceType } from 'state/workspaces/model';
import { getTabIsFrozen } from 'state/workspaces/tabs/tabs.selector';

export type TradeInformationOwnProps = {
  className?: string;
  tabId: string;
  children?: ReactNode;
  isModification: boolean;
};

export type TradeInformationStateProps = {
  quantity: number | undefined;
  cashAmount: number | undefined;
  denomination?: number;
  currency?: string;
  tradeWay: TradeWay;
  needCustodian: boolean;
  selectedCustodian?: Custodian;
  disabledTradeWays: readonly TradeWay[];
  isFrozen: boolean;
  tradeSizeInputMode: TradeSizeInputMode;
  effectiveCashAmount?: number;
  minQuantity?: number;
  maxQuantity?: number;
  lot?: number;
  remaining?: number;
};
export type TradeInformationDispatchProps = {
  onQuantityChanged: (value: number | undefined) => void;
  onQuantityReset: () => void;
  onCashAmountChanged: (value: number | undefined) => void;
  onCashAmountReset: () => void;
  onTradeSizeInputModeChanged: () => void;
  onTradeWayChanged?: (value: TradeWay) => void;
  onCustodianChanged: (value?: Custodian | null) => void;
};

export type TradeInformationProps = TradeInformationDispatchProps &
  TradeInformationStateProps &
  TradeInformationOwnProps;

export const secondaryMarketTradeInformationConnectFactory = (
  referentialSelectors: ReferentialSelector,
  formInputSelectors: LiveInputFormSelectors | LimitOrderInputFormSelectors,
  secondaryMarketSelectors: LiveSelectors | LimitOrderSelectors,
  secondaryMarketThunks: LiveThunks | LimitOrderThunks,
  workspaceType: SecondaryWorkspaceType,
) => {
  const mapStateToProps: MapStateToPropsFactory<
    TradeInformationStateProps,
    TradeInformationOwnProps,
    AppState
  > = () => {
    const getDisabledTradeWays = createSelector([referentialSelectors.getTradeAuthorization], tradeAuthorization =>
      referentialSelectors.getDisabledTradeWays(tradeAuthorization),
    );

    return (state: AppState, { tabId }: TradeInformationOwnProps): TradeInformationStateProps => {
      const selectedCustodian = secondaryMarketSelectors.getFormCustodian(state, tabId);
      const denomination = referentialSelectors.getDenomination(state, tabId);
      const disabledTradeWays = getDisabledTradeWays(state, tabId);
      const currency = referentialSelectors.getCurrency(state, tabId);
      const clientType = secondaryMarketSelectors.getFormClientType(state, tabId);
      const isOtherClientType = clientType === 'Other';
      const tradeWay = secondaryMarketSelectors.getFormTradeWay(state, tabId)!;
      const quantity = secondaryMarketSelectors.getFormQuantity(state, tabId);
      const cashAmount = secondaryMarketSelectors.getFormCashAmount(state, tabId);
      const isFrozen = getTabIsFrozen(state, tabId) ?? false;
      const tradeSizeInputMode = formInputSelectors.getFormTradeSizeInputMode(state, tabId);
      const effectiveCashAmount = secondaryMarketSelectors.getEffectiveCashAmount(state, tabId);
      return {
        denomination,
        quantity,
        cashAmount,
        currency,
        needCustodian: isOtherClientType,
        tradeWay,
        disabledTradeWays,
        isFrozen,
        tradeSizeInputMode,
        effectiveCashAmount,
        selectedCustodian,
        lot: referentialSelectors.getLotSize(state, tabId),
        maxQuantity: secondaryMarketSelectors.getMaxQuantity(state, tabId),
        minQuantity: referentialSelectors.getMinQuantity(state, tabId),
        remaining: referentialSelectors.getRemaining(state, tabId),
      };
    };
  };

  const mapDispatchToProps = (
    dispatch: ThunkDispatch,
    { tabId }: TradeInformationOwnProps,
  ): TradeInformationDispatchProps => {
    return {
      onCashAmountChanged: value => {
        dispatch(secondaryMarketThunks.cashAmountChangedThunk(tabId, value));
      },
      onCashAmountReset: () => {
        dispatch(secondaryMarketThunks.cashAmountResetThunk(tabId));
      },
      onTradeSizeInputModeChanged: () => {
        dispatch(secondaryMarketThunks.tradeSizeInputModeChangedThunk(tabId));
      },
      onQuantityChanged: value => {
        dispatch(secondaryMarketThunks.quantityChangedThunk(tabId, value));
      },
      onQuantityReset: () => {
        dispatch(secondaryMarketThunks.quantityResetThunk(tabId));
      },
      onTradeWayChanged: value => {
        dispatch(secondaryMarketThunks.tradeWayChangedThunk(tabId, value));
      },
      onCustodianChanged: value => {
        dispatch(secondaryMarketThunks.custodianSelectedThunk(tabId, workspaceType, value));
      },
    };
  };

  return { mapStateToProps, mapDispatchToProps };
};
